import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { gsap } from 'gsap';
import { X, Send, User, Loader2, MessageCircle } from 'lucide-react';
import { useComments, useCreateComment } from '@/hooks/usePost';
import { useAuth } from '@/providers/AuthProvider';
import { processAvatarUrl } from '@/lib/mediaUtils';
import { getPostUser } from '@/components/PostCard';
import CommentItem from '@/components/CommentItem';
import type { Post } from '@/types';

export interface CommentModalProps {
  postUlid: string;
  onClose: () => void;
  onProfileClick?: (username: string) => void;
}

/** Tracks which comment we're replying to */
interface ReplyTarget {
  ulid: string;
  username: string;
}

export default function CommentModal({ postUlid, onClose, onProfileClick }: CommentModalProps) {
  const { user } = useAuth();
  const [newComment, setNewComment] = useState('');
  const [replyTarget, setReplyTarget] = useState<ReplyTarget | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const {
    data: commentsData,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useComments(postUlid);
  const createCommentMutation = useCreateComment();

  const comments = commentsData?.pages.flatMap((page) => page.comments) ?? [];

  // Slide-up animation on mount
  useEffect(() => {
    if (modalRef.current) {
      gsap.fromTo(
        modalRef.current,
        { y: '100%' },
        { y: 0, duration: 0.3, ease: 'power2.out' }
      );
    }
  }, []);

  // Infinite scroll for comments
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleClose = () => {
    if (modalRef.current) {
      gsap.to(modalRef.current, {
        y: '100%',
        duration: 0.3,
        ease: 'power2.inOut',
        onComplete: onClose,
      });
    }
  };

  const handleSubmit = () => {
    if (!newComment.trim()) return;

    createCommentMutation.mutate(
      {
        postUlid,
        body: newComment,
        parentId: replyTarget?.ulid,
      },
      {
        onSuccess: () => {
          setNewComment('');
          setReplyTarget(null);
        },
      }
    );
  };

  const handleReply = (comment: Post) => {
    const author = getPostUser(comment);
    setReplyTarget({
      ulid: comment.ulid ?? comment.id,
      username: author.username,
    });
    // Pre-fill input with @mention and focus
    setNewComment(`@${author.username} `);
    inputRef.current?.focus();
  };

  const cancelReply = () => {
    setReplyTarget(null);
    setNewComment('');
  };

  // User avatar
  const userRecord = user as Record<string, unknown> | null;
  const displayName = (userRecord?.displayName as string) || (userRecord?.name as string) || user?.username || 'You';
  const rawAvatar = (userRecord?.avatarUrl as string) || (userRecord?.avatar as string) || '';
  const avatarUrl = processAvatarUrl(rawAvatar) || '';

  return createPortal(
    <div className="fixed inset-0 z-50 bg-black/60" onClick={handleClose}>
      <div
        ref={modalRef}
        className="absolute bottom-0 left-0 right-0 bg-[#141821] rounded-t-[24px] max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Handle bar */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-10 h-1 bg-[#2A2F3A] rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
          <h3 className="font-semibold text-[#F5F7FF]">
            Comments ({isLoading ? '…' : comments.length})
          </h3>
          <button onClick={handleClose} className="text-[#A7B0C8]">
            <X size={22} />
          </button>
        </div>

        {/* Comments List */}
        <div className="overflow-y-auto flex-1 p-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={28} className="animate-spin text-[#A38BFF]" />
            </div>
          ) : comments.length === 0 ? (
            <div className="text-center py-8">
              <MessageCircle size={48} className="text-[#2A2F3A] mx-auto mb-3" />
              <p className="text-[#A7B0C8]">No comments yet</p>
              <p className="text-sm text-[#A7B0C8]">Be the first to comment!</p>
            </div>
          ) : (
            <div className="divide-y divide-white/[0.04]">
              {comments
                .filter((c) => {
                  const rec = c as Record<string, unknown>;
                  // Root comments have parentUlid === rootUlid (direct replies to post)
                  return rec.parentUlid === rec.rootUlid;
                })
                .map((comment) => (
                  <CommentItem
                    key={comment.ulid ?? comment.id}
                    comment={comment}
                    onProfileClick={onProfileClick}
                    onReply={handleReply}
                    allComments={comments}
                    depth={0}
                  />
                ))}
              {/* Infinite scroll sentinel */}
              <div ref={sentinelRef} className="h-1" />
              {isFetchingNextPage && (
                <div className="flex justify-center py-4">
                  <Loader2 size={20} className="animate-spin text-[#A38BFF]" />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Comment Input */}
        <div className="border-t border-white/[0.06] bg-[#0B0D10] pb-safe">
          {/* Replying to indicator */}
          {replyTarget && (
            <div className="flex items-center justify-between px-4 py-2 bg-[#1A1F2A]">
              <span className="text-xs text-[#A7B0C8]">
                Replying to <span className="text-[#A38BFF]">@{replyTarget.username}</span>
              </span>
              <button
                onClick={cancelReply}
                className="text-xs text-[#A7B0C8] hover:text-[#F5F7FF]"
              >
                Cancel
              </button>
            </div>
          )}
          <div className="flex items-center gap-3 p-4">
            <div className="w-9 h-9 rounded-full overflow-hidden bg-[#2A2F3A] flex items-center justify-center flex-shrink-0">
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt={displayName}
                  className="w-full h-full object-cover"
                />
              ) : (
                <User size={18} className="text-[#A7B0C8]" />
              )}
            </div>
            <div className="flex-1">
              <input
                ref={inputRef}
                type="text"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder={replyTarget ? `Reply to @${replyTarget.username}...` : 'Add a comment...'}
                className="w-full bg-[#141821] rounded-full px-4 py-2.5 text-sm text-[#F5F7FF] placeholder-[#A7B0C8] outline-none border border-white/[0.08]"
                onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
              />
            </div>
            <button
              onClick={handleSubmit}
              disabled={!newComment.trim() || createCommentMutation.isPending}
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                newComment.trim()
                  ? 'bg-[#A38BFF] text-[#0B0D10]'
                  : 'bg-[#2A2F3A] text-[#A7B0C8]'
              }`}
            >
              {createCommentMutation.isPending ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Send size={18} />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
